import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { RowActions } from "@/shared/ui/RowActions";
import type { PerDiemPolicyDTO } from "../../domain/contracts";

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

type PerDiemPolicyTableProps = {
  data: PerDiemPolicyDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (item: PerDiemPolicyDTO) => void;
  onDelete: (id: string) => void | Promise<void>;
};

export function PerDiemPolicyTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
}: PerDiemPolicyTableProps) {
  const canUpdate = useActionPermission(["performance-per-diem:update"]);
  const canDelete = useActionPermission(["performance-per-diem:delete"]);

  const columns: TanstackReactTable.ColumnDef<PerDiemPolicyDTO>[] = [
    {
      id: "code",
      header: "ລະຫັດ",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.code}</span>
      ),
    },
    createSortableColumn<PerDiemPolicyDTO>("name", "ຊື່"),
    {
      id: "amount",
      header: "ອັດຕາ",
      cell: ({ row }) =>
        row.original.basis === "per_person_per_event"
          ? `${formatMoney(row.original.amountPerEvent ?? row.original.amountPerDay)} / ຕື່ມງານ`
          : `${formatMoney(row.original.amountPerDay)} / ວັນ`,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge
          variant={row.original.status === "active" ? "default" : "secondary"}
        >
          {row.original.status === "active" ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
        </Badge>
      ),
    },
    {
      id: "actions",
      size: 80,
      header: "",
      cell: ({ row }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void | Promise<void>;
        }[] = [];

        if (canUpdate) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (canDelete) {
          actions.push({
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            variant: "destructive",
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບນະໂຍບາຍ",
                description: `ລຶບ "${row.original.name}"?`,
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) await onDelete(row.original.id);
            },
          });
        }
        if (actions.length === 0) return null;
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<PerDiemPolicyDTO, unknown>
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      noDataMessage="ບໍ່ພົບນະໂຍບາຍ"
    />
  );
}
